import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Truck, Edit, Trash2, ShieldCheck, Phone, Mail, AlertCircle } from 'lucide-react';
import {
  listSuppliers, createSupplier, updateSupplier, deleteSupplier, getSupplierTypes,
} from '../lib/api';

const FBO_HINT = "Optional. Ireland EC approval (e.g. IE-1234-EC). Required by FSAI for animal-origin foods.";
const FBO_REGEX = /^IE[\s-]?\d{1,6}[\s-]?EC$/i;

const TYPE_LABELS = {
  raw_milk: 'Raw Milk',
  cream: 'Cream',
  salt: 'Salt',
  rennet: 'Rennet',
  culture: 'Culture',
  packaging: 'Packaging',
  labelling: 'Labelling',
  courier: 'Courier / Transport',
  ingredient: 'Other Ingredient',
  equipment: 'Equipment',
  lab_services: 'Lab Services',
  other: 'Other',
};

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-800',
  on_hold: 'bg-amber-100 text-amber-800',
  decommissioned: 'bg-zinc-100 text-zinc-600',
};

const emptyForm = {
  name: '',
  trading_name: '',
  supplier_type: 'raw_milk',
  fbo_approval_number: '',
  address_line1: '',
  address_line2: '',
  city: '',
  county: '',
  postcode: '',
  country: 'Ireland',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  incident_contact_name: '',
  incident_contact_email: '',
  incident_contact_phone: '',
  notes: '',
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [types, setTypes] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fboWarning, setFboWarning] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (search) params.search = search;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.supplier_type = filterType;
      const { data } = await listSuppliers(params);
      setSuppliers(data.suppliers || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSupplierTypes();
        setTypes(data.types || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setFboWarning('');
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '',
      trading_name: s.trading_name || '',
      supplier_type: s.supplier_type || 'other',
      fbo_approval_number: s.fbo_approval_number || '',
      address_line1: s.address_line1 || '',
      address_line2: s.address_line2 || '',
      city: s.city || '',
      county: s.county || '',
      postcode: s.postcode || '',
      country: s.country || 'Ireland',
      contact_name: s.contact_name || '',
      contact_email: s.contact_email || '',
      contact_phone: s.contact_phone || '',
      incident_contact_name: s.incident_contact_name || '',
      incident_contact_email: s.incident_contact_email || '',
      incident_contact_phone: s.incident_contact_phone || '',
      notes: s.notes || '',
    });
    setFboWarning('');
    setDialogOpen(true);
  };

  const updateField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (key === 'fbo_approval_number') {
      const trimmed = (val || '').trim();
      if (trimmed && !FBO_REGEX.test(trimmed)) {
        setFboWarning('Hint: FSAI format is usually IE-1234-EC. We accept other identifiers but the inspector may flag.');
      } else {
        setFboWarning('');
      }
    }
  };

  const handleSave = async () => {
    if (!form.name || form.name.length < 2) {
      toast.error('Supplier name is required (min 2 chars)');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateSupplier(editing.id, form);
        toast.success('Supplier updated');
      } else {
        await createSupplier(form);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Decommission supplier "${s.name}"? They will be hidden from active lists but the record will be retained for traceability.`)) return;
    try {
      await deleteSupplier(s.id);
      toast.success('Supplier decommissioned');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="w-6 h-6 text-teal-700" />
            Suppliers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            FSAI / Reg 853/2004 / BRC — one-step-back traceability register. Records the businesses supplying you with raw materials, packaging, services and consumables.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="btn-add-supplier">
          <Plus className="w-4 h-4 mr-1" /> New Supplier
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, trading name or FBO number"
                  className="pl-8"
                  data-testid="supplier-search"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="decommissioned">Decommissioned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto text-sm text-muted-foreground">{total} supplier(s)</div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : suppliers.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center text-muted-foreground">
                No suppliers yet. Click "New Supplier" to start your one-step-back register.
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>FBO #</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                      <TableCell>
                        <div className="font-medium">{s.name}</div>
                        {s.trading_name && (
                          <div className="text-xs text-muted-foreground">{s.trading_name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{TYPE_LABELS[s.supplier_type] || s.supplier_type}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {s.fbo_approval_number ? (
                          <span className="inline-flex items-center gap-1">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                            {s.fbo_approval_number}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.city || s.county ? `${s.city || ''}${s.city && s.county ? ', ' : ''}${s.county || ''}` : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.contact_name && <div className="font-medium">{s.contact_name}</div>}
                        {s.contact_email && (
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {s.contact_email}
                          </div>
                        )}
                        {s.contact_phone && (
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {s.contact_phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[s.status] || ''}>{s.status?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`btn-edit-supplier-${s.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(s)} data-testid={`btn-delete-supplier-${s.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Supplier' : 'New Supplier'}</DialogTitle>
            <DialogDescription>
              Records the businesses supplying you with raw materials, packaging or services. FSAI requires the name, address and (for animal-origin foods) the EC approval number.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => updateField('name', e.target.value)} data-testid="supplier-name" />
            </div>
            <div>
              <Label>Trading Name</Label>
              <Input value={form.trading_name} onChange={(e) => updateField('trading_name', e.target.value)} />
            </div>
            <div>
              <Label>Supplier Type</Label>
              <Select value={form.supplier_type} onValueChange={(v) => updateField('supplier_type', v)}>
                <SelectTrigger data-testid="supplier-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t] || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>FBO Approval Number</Label>
              <Input
                value={form.fbo_approval_number}
                onChange={(e) => updateField('fbo_approval_number', e.target.value)}
                placeholder="IE-1234-EC"
                data-testid="supplier-fbo"
              />
              {fboWarning ? (
                <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {fboWarning}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">{FBO_HINT}</p>
              )}
            </div>

            <div className="col-span-2">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-3 mb-1">Address</h4>
            </div>
            <div className="col-span-2">
              <Label>Address Line 1</Label>
              <Input value={form.address_line1} onChange={(e) => updateField('address_line1', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Address Line 2</Label>
              <Input value={form.address_line2} onChange={(e) => updateField('address_line2', e.target.value)} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => updateField('city', e.target.value)} />
            </div>
            <div>
              <Label>County</Label>
              <Input value={form.county} onChange={(e) => updateField('county', e.target.value)} />
            </div>
            <div>
              <Label>Postcode</Label>
              <Input value={form.postcode} onChange={(e) => updateField('postcode', e.target.value)} />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => updateField('country', e.target.value)} />
            </div>

            <div className="col-span-2">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-3 mb-1">Primary Contact</h4>
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} />
            </div>

            <div className="col-span-2">
              <h4 className="text-xs font-semibold uppercase text-muted-foreground mt-3 mb-1">
                Incident / QA Contact <span className="font-normal text-muted-foreground normal-case">(used for recalls)</span>
              </h4>
            </div>
            <div>
              <Label>Incident Contact Name</Label>
              <Input value={form.incident_contact_name} onChange={(e) => updateField('incident_contact_name', e.target.value)} />
            </div>
            <div>
              <Label>Incident Email</Label>
              <Input type="email" value={form.incident_contact_email} onChange={(e) => updateField('incident_contact_email', e.target.value)} />
            </div>
            <div>
              <Label>Incident Phone</Label>
              <Input value={form.incident_contact_phone} onChange={(e) => updateField('incident_contact_phone', e.target.value)} />
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => updateField('notes', e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-supplier">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing ? 'Save Changes' : 'Create Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
